import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaFilter, FaEye, FaTrash, FaUsers, FaTimes, FaIdCard, FaFileAlt, FaExternalLinkAlt, FaHeadset, FaPlus, FaUserTie, FaBell } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';
import './Dashboard.css';
import './AdminClients.css';

const API = 'https://aayushrealproperties.in/nimaibackend/public/api';

const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ status }) => (
    <span className={`status-badge ${status}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
);

const AdminClients = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [statusEdit, setStatusEdit] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [adminPanelUrl, setAdminPanelUrl] = useState('');
    const [planStartDate, setPlanStartDate] = useState('');
    const [planName, setPlanName] = useState('');
    const [planDuration, setPlanDuration] = useState('');
    const [planPrice, setPlanPrice] = useState('');
    const [saving, setSaving] = useState(false);

    // Create Client States
    const emptyClient = { first_name: '', last_name: '', email: '', mobile: '', address: '', project_type: 'Website Development', project_name: '', selected_plan: '', plan_duration: '', custom_plan_price: '', plan_start_date: '' };
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState(emptyClient);
    const [creating, setCreating] = useState(false);

    // KYC Action States
    const [kycUpdating, setKycUpdating] = useState(false);
    const [showRejectInput, setShowRejectInput] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    // POC state
    const [pocs, setPocs] = useState([]);
    const [pocLoading, setPocLoading] = useState(false);
    const [pocForm, setPocForm] = useState({ name: '', role: '', email: '', phone: '', whatsapp: '' });
    const [pocAdding, setPocAdding] = useState(false);
    const [showPocForm, setShowPocForm] = useState(false);

    // Payment Reminder States
    const [reminderClient, setReminderClient] = useState(null);
    const [reminderAmount, setReminderAmount] = useState('');
    const [reminderNotes, setReminderNotes] = useState('');
    const [sendingReminder, setSendingReminder] = useState(false);

    const token = localStorage.getItem('admin_token');
    const headers = { Authorization: `Bearer ${token}`, Accept: 'application/json' };

    useEffect(() => { fetchClients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchClients = async () => {
        setLoading(true);
        try {
            const { ok, data } = await safeFetch(`${API}/clients`, { headers });
            if (ok) setClients(Array.isArray(data) ? data : data.data || []);
            else toast.error('Failed to fetch clients');
        } catch {
            toast.error('Network error');
        } finally {
            setLoading(false);
        }
    };

    const openDetail = (client) => {
        setSelected(client);
        setStatusEdit(client.status);
        setWebsiteUrl(client.website_url || '');
        setAdminPanelUrl(client.admin_panel_url || '');
        setPlanStartDate(client.plan_start_date || '');
        setPlanName(client.selected_plan || '');
        setPlanDuration(client.plan_duration || '');
        setPlanPrice(client.custom_plan_price || '');
        setPocs([]);
        setShowPocForm(false);
        setPocForm({ name: '', role: '', email: '', phone: '', whatsapp: '' });
        fetchPocs(client.id);
        setShowRejectInput(false);
        setRejectReason('');
    };

    const fetchPocs = async (clientId) => {
        setPocLoading(true);
        try {
            const { ok, data } = await safeFetch(`${API}/clients/${clientId}/poc`, { headers });
            if (ok) setPocs(data.data || []);
        } catch {}
        finally { setPocLoading(false); }
    };

    const handleAddPoc = async () => {
        if (!pocForm.name.trim() || !pocForm.role.trim()) {
            toast.error('Name and Role are required');
            return;
        }
        setPocAdding(true);
        try {
            const { ok, data } = await safeFetch(`${API}/clients/${selected.id}/poc`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(pocForm),
            });
            if (ok) {
                setPocs(prev => [...prev, data.data]);
                setPocForm({ name: '', role: '', email: '', phone: '', whatsapp: '' });
                setShowPocForm(false);
                toast.success('POC added');
            } else {
                toast.error(data.message || 'Failed to add POC');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setPocAdding(false);
        }
    };

    const handleDeletePoc = async (pocId) => {
        if (!window.confirm('Remove this POC?')) return;
        try {
            const { ok } = await safeFetch(`${API}/poc/${pocId}`, { method: 'DELETE', headers });
            if (ok) {
                setPocs(prev => prev.filter(p => p.id !== pocId));
                toast.success('POC removed');
            } else {
                toast.error('Failed to remove');
            }
        } catch {
            toast.error('Network error');
        }
    };

    const handleStatusSave = async () => {
        setSaving(true);
        try {
            const { ok, data } = await safeFetch(`${API}/clients/${selected.id}`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: statusEdit,
                    website_url: websiteUrl,
                    admin_panel_url: adminPanelUrl,
                    plan_start_date: planStartDate || null,
                    selected_plan: planName || null,
                    plan_duration: planName ? (planDuration || 1) : null,
                    custom_plan_price: planPrice === '' ? null : planPrice,
                }),
            });
            if (ok) {
                const patch = { status: statusEdit, website_url: websiteUrl, admin_panel_url: adminPanelUrl, plan_start_date: planStartDate, selected_plan: planName || null, plan_duration: planName ? (planDuration || 1) : null, custom_plan_price: planPrice === '' ? null : planPrice };
                setClients(prev => prev.map(c => c.id === selected.id ? { ...c, ...patch } : c));
                setSelected(prev => ({ ...prev, ...patch }));
                toast.success('Client updated');
            } else {
                toast.error(data.message || 'Failed to update');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setSaving(false);
        }
    };

    const handleKycStatusUpdate = async (status, reason = '') => {
        setKycUpdating(true);
        try {
            const { ok, data } = await safeFetch(`${API}/clients/${selected.id}/kyc`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    kyc_status: status,
                    kyc_reject_reason: reason
                }),
            });
            if (ok) {
                toast.success(`KYC status updated to ${status}`);
                setClients(prev => prev.map(c => c.id === selected.id ? { ...c, kyc_status: status, kyc_reject_reason: status === 'rejected' ? reason : null } : c));
                setSelected(prev => ({ ...prev, kyc_status: status, kyc_reject_reason: status === 'rejected' ? reason : null }));
                setShowRejectInput(false);
                setRejectReason('');
            } else {
                toast.error(data.message || 'Failed to update KYC status');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setKycUpdating(false);
        }
    };

    const handleSendReminder = async (e) => {
        e.preventDefault();
        if (!reminderAmount) return;
        setSendingReminder(true);
        try {
            const { ok, data } = await safeFetch(`${API}/clients/${reminderClient.id}/send-reminder`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: reminderAmount,
                    notes: reminderNotes
                }),
            });
            if (ok) {
                toast.success('Payment reminder sent successfully!');
                setReminderClient(null);
                setReminderAmount('');
                setReminderNotes('');
            } else {
                toast.error(data.message || 'Failed to send reminder');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setSendingReminder(false);
        }
    };

    const handleCreateClient = async (e) => {
        e.preventDefault();
        const required = ['first_name', 'last_name', 'email', 'mobile', 'address', 'project_name'];
        for (const f of required) {
            if (!String(createForm[f] || '').trim()) {
                toast.error('Please fill all required fields');
                return;
            }
        }
        setCreating(true);
        try {
            const payload = {
                first_name: createForm.first_name,
                last_name: createForm.last_name,
                email: createForm.email,
                mobile: createForm.mobile,
                address: createForm.address,
                project_type: createForm.project_type,
                project_name: createForm.project_name,
            };
            // Optional plan assignment
            if (createForm.selected_plan) {
                payload.selected_plan = createForm.selected_plan;
                payload.plan_duration = createForm.plan_duration || 1;
                payload.custom_plan_price = createForm.custom_plan_price === '' ? null : createForm.custom_plan_price;
                payload.plan_start_date = createForm.plan_start_date || null;
            }
            const { ok, data } = await safeFetch(`${API}/clients`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (ok) {
                if (data.data) setClients(prev => [data.data, ...prev]);
                toast.success(data.message || 'Client created — credentials emailed');
                setShowCreate(false);
                setCreateForm(emptyClient);
            } else {
                const firstErr = data.errors ? Object.values(data.errors)[0][0] : data.message;
                toast.error(firstErr || 'Failed to create client');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this client? This cannot be undone.')) return;
        try {
            const { ok, data } = await safeFetch(`${API}/clients/${id}`, {
                method: 'DELETE',
                headers,
            });
            if (ok) {
                setClients(prev => prev.filter(c => c.id !== id));
                if (selected?.id === id) setSelected(null);
                toast.success('Client deleted');
            } else {
                toast.error(data.message || 'Failed to delete');
            }
        } catch {
            toast.error('Network error');
        }
    };

    const filtered = clients.filter(c =>
        `${c.first_name} ${c.last_name} ${c.email} ${c.project_name}`
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Onboarded <span>Clients</span></h1>
                        <p>View and manage all clients who submitted the onboarding form.</p>
                    </div>
                </header>

                {/* Controls */}
                <div className="clients-controls">
                    <div className="clients-search">
                        <FaSearch />
                        <input
                            placeholder="Search by name, email or project..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="clients-count">
                        <FaFilter /> Total: {filtered.length}
                    </div>
                    <button className="clients-add-btn" onClick={() => { setCreateForm(emptyClient); setShowCreate(true); }}>
                        <FaPlus /> Add Client
                    </button>
                    <button className="clients-refresh" onClick={fetchClients}>Refresh</button>
                </div>

                {/* Table */}
                <div className="clients-table-wrap">
                    {loading ? (
                        <div className="clients-loading">
                            <p>Loading clients...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="clients-empty">
                            <FaUsers />
                            <h3>No clients found</h3>
                            <p>No onboarding submissions yet. Share the onboarding link with your clients.</p>
                        </div>
                    ) : (
                        <table className="clients-table">
                            <thead>
                                <tr>
                                    <th>Client</th>
                                    <th>Mobile</th>
                                    <th>Project</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Joined</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((client, i) => (
                                    <motion.tr
                                        key={client.id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.04 }}
                                    >
                                        <td>
                                            <div className="client-name-cell">
                                                <div className="client-avatar">
                                                    {client.first_name?.charAt(0)}{client.last_name?.charAt(0)}
                                                </div>
                                                <div>
                                                    <div className="client-full-name">{client.first_name} {client.last_name}</div>
                                                    <div className="client-email-sub">{client.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{client.mobile}</td>
                                        <td>{client.project_name}</td>
                                        <td><span className="type-badge">{client.project_type}</span></td>
                                        <td><StatusBadge status={client.status} /></td>
                                        <td>{formatDate(client.created_at)}</td>
                                        <td>
                                            <button className="tbl-btn view" title="View Details" onClick={() => openDetail(client)}>
                                                <FaEye />
                                            </button>
                                            <button className="tbl-btn reminder" title="Send Payment Reminder" onClick={() => { setReminderClient(client); setReminderAmount(''); setReminderNotes(''); }}>
                                                <FaBell />
                                            </button>
                                            <button className="tbl-btn del" title="Delete" onClick={() => handleDelete(client.id)}>
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Detail Modal */}
                <AnimatePresence>
                    {selected && (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelected(null)}
                        >
                            <motion.div
                                className="client-detail-modal"
                                initial={{ scale: 0.92, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.92, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                            >
                                <div className="modal-top">
                                    <h2>Client <span>Details</span></h2>
                                    <button className="modal-close" onClick={() => setSelected(null)}><FaTimes /></button>
                                </div>

                                {selected.project_logo && (
                                    <div className="modal-logo">
                                        <img
                                            src={`https://aayushrealproperties.in/nimaibackend/public/storage/${selected.project_logo}`}
                                            alt="Project Logo"
                                        />
                                    </div>
                                )}

                                <div className="modal-grid">
                                    <div className="modal-field">
                                        <label>Full Name</label>
                                        <p>{selected.first_name} {selected.last_name}</p>
                                    </div>
                                    <div className="modal-field">
                                        <label>Email</label>
                                        <p>{selected.email}</p>
                                    </div>
                                    <div className="modal-field">
                                        <label>Mobile</label>
                                        <p>{selected.mobile}</p>
                                    </div>
                                    <div className="modal-field">
                                        <label>Project Type</label>
                                        <p>{selected.project_type}</p>
                                    </div>
                                    <div className="modal-field">
                                        <label>Project Name</label>
                                        <p>{selected.project_name}</p>
                                    </div>
                                    <div className="modal-field">
                                        <label>Joined</label>
                                        <p>{formatDate(selected.created_at)}</p>
                                    </div>
                                    <div className="modal-field" style={{ gridColumn: '1 / -1' }}>
                                        <label>Address</label>
                                        <p>{selected.address}</p>
                                    </div>
                                </div>

                                {selected.features?.length > 0 && (
                                    <div className="modal-features">
                                        <label>Features</label>
                                        <div className="features-tags">
                                            {selected.features.map((f, i) => (
                                                <span key={i} className="feature-tag">{f}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* KYC Document */}
                                {selected.kyc_document && (
                                    <div className="modal-kyc">
                                        <label><FaIdCard /> KYC Document</label>
                                        <div className="kyc-doc-row">
                                            <span className="kyc-doc-type">{selected.kyc_document_type}</span>
                                            {selected.kyc_document.endsWith('.pdf') ? (
                                                <a
                                                    href={`https://aayushrealproperties.in/nimaibackend/public/storage/${selected.kyc_document}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="kyc-view-btn"
                                                >
                                                    <FaFileAlt /> View PDF <FaExternalLinkAlt />
                                                </a>
                                            ) : (
                                                <a
                                                    href={`https://aayushrealproperties.in/nimaibackend/public/storage/${selected.kyc_document}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="kyc-view-btn"
                                                >
                                                    <FaExternalLinkAlt /> View Document
                                                </a>
                                            )}
                                        </div>
                                        {!selected.kyc_document.endsWith('.pdf') && (
                                            <img
                                                src={`https://aayushrealproperties.in/nimaibackend/public/storage/${selected.kyc_document}`}
                                                alt="KYC Document"
                                                className="kyc-doc-preview"
                                            />
                                        )}

                                        {/* KYC Status & Approve/Reject Actions */}
                                        <div className="kyc-status-container">
                                            <div className="kyc-status-header">
                                                <span className="kyc-status-label">KYC Status:</span>
                                                <span className={`kyc-badge-pill ${selected.kyc_status || 'pending'}`}>
                                                    {(selected.kyc_status || 'pending').toUpperCase()}
                                                </span>
                                            </div>

                                            {selected.kyc_status === 'rejected' && selected.kyc_reject_reason && (
                                                <div className="kyc-rejection-reason">
                                                    <strong>Reason: </strong>
                                                    <span>{selected.kyc_reject_reason}</span>
                                                </div>
                                            )}

                                            <div className="kyc-action-buttons">
                                                {selected.kyc_status !== 'approved' && (
                                                    <button
                                                        type="button"
                                                        className="kyc-btn approve"
                                                        onClick={() => handleKycStatusUpdate('approved')}
                                                        disabled={kycUpdating}
                                                    >
                                                        Approve KYC
                                                    </button>
                                                )}
                                                {selected.kyc_status !== 'rejected' && !showRejectInput && (
                                                    <button
                                                        type="button"
                                                        className="kyc-btn reject"
                                                        onClick={() => setShowRejectInput(true)}
                                                        disabled={kycUpdating}
                                                    >
                                                        Reject KYC
                                                    </button>
                                                )}
                                                {selected.kyc_status === 'approved' && !showRejectInput && (
                                                    <button
                                                        type="button"
                                                        className="kyc-btn reject"
                                                        onClick={() => setShowRejectInput(true)}
                                                        disabled={kycUpdating}
                                                    >
                                                        Reject KYC (Revoke)
                                                    </button>
                                                )}
                                            </div>

                                            {showRejectInput && (
                                                <div className="kyc-reject-form">
                                                    <textarea
                                                        className="kyc-reject-textarea"
                                                        placeholder="Provide the reason for rejecting KYC... *"
                                                        value={rejectReason}
                                                        onChange={e => setRejectReason(e.target.value)}
                                                        rows={2}
                                                    />
                                                    <div className="kyc-reject-actions">
                                                        <button
                                                            type="button"
                                                            className="kyc-reject-submit-btn"
                                                            onClick={() => {
                                                                if (!rejectReason.trim()) {
                                                                    toast.error('Rejection reason is required');
                                                                    return;
                                                                }
                                                                handleKycStatusUpdate('rejected', rejectReason);
                                                            }}
                                                            disabled={kycUpdating}
                                                        >
                                                            Confirm Reject
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="kyc-reject-cancel-btn"
                                                            onClick={() => {
                                                                setShowRejectInput(false);
                                                                setRejectReason('');
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Point of Contact */}
                                <div className="modal-poc-section">
                                    <div className="modal-poc-header">
                                        <label><FaHeadset /> Point of Contact</label>
                                        <button className="poc-add-toggle-btn" onClick={() => setShowPocForm(p => !p)}>
                                            <FaPlus /> {showPocForm ? 'Cancel' : 'Add POC'}
                                        </button>
                                    </div>

                                    {showPocForm && (
                                        <div className="poc-form">
                                            <div className="poc-form-grid">
                                                <input placeholder="Name *" value={pocForm.name} onChange={e => setPocForm(p => ({ ...p, name: e.target.value }))} />
                                                <input placeholder="Role *" value={pocForm.role} onChange={e => setPocForm(p => ({ ...p, role: e.target.value }))} />
                                                <input placeholder="Email" value={pocForm.email} onChange={e => setPocForm(p => ({ ...p, email: e.target.value }))} />
                                                <input placeholder="Phone" value={pocForm.phone} onChange={e => setPocForm(p => ({ ...p, phone: e.target.value }))} />
                                                <input placeholder="WhatsApp Number" value={pocForm.whatsapp} onChange={e => setPocForm(p => ({ ...p, whatsapp: e.target.value }))} />
                                            </div>
                                            <button className="poc-save-btn" onClick={handleAddPoc} disabled={pocAdding}>
                                                {pocAdding ? 'Saving…' : 'Save POC'}
                                            </button>
                                        </div>
                                    )}

                                    {pocLoading ? (
                                        <p className="poc-loading">Loading…</p>
                                    ) : pocs.length === 0 ? (
                                        <p className="poc-empty">No POC assigned yet. Click "Add POC" to assign one.</p>
                                    ) : (
                                        <div className="poc-list">
                                            {pocs.map(p => (
                                                <div key={p.id} className="poc-card">
                                                    <div className="poc-card-avatar"><FaUserTie /></div>
                                                    <div className="poc-card-info">
                                                        <div className="poc-card-name">{p.name}</div>
                                                        <div className="poc-card-role">{p.role}</div>
                                                        {p.email && <div className="poc-card-detail">{p.email}</div>}
                                                        {p.phone && <div className="poc-card-detail">{p.phone}</div>}
                                                    </div>
                                                    <button className="poc-delete-btn" onClick={() => handleDeletePoc(p.id)} title="Remove">
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Website Details */}
                                <div className="modal-website-section">
                                    <div className="modal-field">
                                        <label>Website URL</label>
                                        <input
                                            className="modal-url-input"
                                            type="url"
                                            placeholder="https://example.com"
                                            value={websiteUrl}
                                            onChange={e => setWebsiteUrl(e.target.value)}
                                        />
                                    </div>
                                    <div className="modal-field">
                                        <label>Admin Panel URL</label>
                                        <input
                                            className="modal-url-input"
                                            type="url"
                                            placeholder="https://example.com/admin"
                                            value={adminPanelUrl}
                                            onChange={e => setAdminPanelUrl(e.target.value)}
                                        />
                                    </div>
                                    <div className="modal-field">
                                        <label>Plan Start Date</label>
                                        <input
                                            className="modal-url-input"
                                            type="date"
                                            value={planStartDate}
                                            onChange={e => setPlanStartDate(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Custom Plan Assignment */}
                                <div className="modal-plan-section">
                                    <label className="modal-plan-title"><FaIdCard /> Plan Assignment</label>
                                    <div className="modal-plan-grid">
                                        <div className="modal-field">
                                            <label>Plan Name</label>
                                            <input
                                                className="modal-url-input"
                                                list="plan-presets"
                                                placeholder="e.g. Custom Plan"
                                                value={planName}
                                                onChange={e => setPlanName(e.target.value)}
                                            />
                                            <datalist id="plan-presets">
                                                <option value=".in Plan" />
                                                <option value=".com Plan" />
                                                <option value="Custom Plan" />
                                            </datalist>
                                        </div>
                                        <div className="modal-field">
                                            <label>Custom Price (₹)</label>
                                            <input
                                                className="modal-url-input"
                                                type="number"
                                                min="0"
                                                step="any"
                                                placeholder="e.g. 1499"
                                                value={planPrice}
                                                onChange={e => setPlanPrice(e.target.value)}
                                            />
                                        </div>
                                        <div className="modal-field">
                                            <label>Duration (months)</label>
                                            <select
                                                className="modal-url-input"
                                                value={planDuration}
                                                onChange={e => setPlanDuration(e.target.value)}
                                            >
                                                <option value="">Select…</option>
                                                <option value="1">1 month</option>
                                                <option value="3">3 months</option>
                                                <option value="6">6 months</option>
                                                <option value="12">12 months</option>
                                            </select>
                                        </div>
                                    </div>
                                    <p className="modal-plan-hint">Assigning a plan only sets it on the client — no invoice or charge is created.</p>
                                </div>

                                <div className="modal-status-row">
                                    <label>Status</label>
                                    <select value={statusEdit} onChange={e => setStatusEdit(e.target.value)}>
                                        <option value="pending">Pending</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                    <button
                                        className="modal-save-btn"
                                        onClick={handleStatusSave}
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving…' : 'Save'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Payment Reminder Modal */}
                <AnimatePresence>
                    {reminderClient && (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="stat-edit-modal"
                                initial={{ y: 50, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 50, opacity: 0 }}
                                style={{ background: '#fff', borderRadius: '30px', padding: '40px', width: '100%', maxWidth: '500px' }}
                            >
                                <div className="modal-header">
                                    <h2>Send Payment Reminder</h2>
                                    <button className="close-btn" onClick={() => setReminderClient(null)}>
                                        <FaTimes />
                                    </button>
                                </div>
                                <form onSubmit={handleSendReminder} className="admin-form">
                                    <div className="form-group" style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Client Email</label>
                                        <input
                                            type="text"
                                            value={reminderClient.email}
                                            disabled
                                            style={{ width: '100%', padding: '15px', border: '2px solid #e2e8f0', borderRadius: '12px', background: '#f1f5f9', color: '#64748b' }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Project Name</label>
                                        <input
                                            type="text"
                                            value={reminderClient.project_name}
                                            disabled
                                            style={{ width: '100%', padding: '15px', border: '2px solid #e2e8f0', borderRadius: '12px', background: '#f1f5f9', color: '#64748b' }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Amount (₹)</label>
                                        <input
                                            type="number"
                                            step="any"
                                            placeholder="e.g. 5000"
                                            value={reminderAmount}
                                            onChange={e => setReminderAmount(e.target.value)}
                                            required
                                            min="1"
                                            style={{ width: '100%', padding: '15px', border: '2px solid #e2e8f0', borderRadius: '12px', background: '#fff', outline: 'none' }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Custom Note / Message (Optional)</label>
                                        <textarea
                                            rows="3"
                                            placeholder="e.g. Milestone 2 payment request"
                                            value={reminderNotes}
                                            onChange={e => setReminderNotes(e.target.value)}
                                            style={{ width: '100%', padding: '15px', border: '2px solid #e2e8f0', borderRadius: '12px', background: '#fff', outline: 'none', resize: 'vertical' }}
                                        ></textarea>
                                    </div>
                                    <div className="modal-footer" style={{ display: 'flex', gap: '15px', marginTop: '30px' }}>
                                        <button type="button" className="btn-secondary" onClick={() => setReminderClient(null)} style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 700, cursor: 'pointer' }}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn-primary" disabled={sendingReminder || !reminderAmount} style={{ flex: 2, padding: '14px', borderRadius: '12px', border: 'none', background: '#0E1629', color: 'white', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {sendingReminder ? 'Sending...' : 'Send Reminder'}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Create Client Modal */}
                <AnimatePresence>
                    {showCreate && (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowCreate(false)}
                        >
                            <motion.div
                                className="client-detail-modal"
                                initial={{ scale: 0.92, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.92, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                            >
                                <div className="modal-top">
                                    <h2>Add <span>Client</span></h2>
                                    <button className="modal-close" onClick={() => setShowCreate(false)}><FaTimes /></button>
                                </div>

                                <form onSubmit={handleCreateClient}>
                                    <div className="modal-grid">
                                        <div className="modal-field">
                                            <label>First Name *</label>
                                            <input className="modal-url-input" value={createForm.first_name} onChange={e => setCreateForm(f => ({ ...f, first_name: e.target.value }))} />
                                        </div>
                                        <div className="modal-field">
                                            <label>Last Name *</label>
                                            <input className="modal-url-input" value={createForm.last_name} onChange={e => setCreateForm(f => ({ ...f, last_name: e.target.value }))} />
                                        </div>
                                        <div className="modal-field">
                                            <label>Email *</label>
                                            <input className="modal-url-input" type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} />
                                        </div>
                                        <div className="modal-field">
                                            <label>Mobile *</label>
                                            <input className="modal-url-input" value={createForm.mobile} onChange={e => setCreateForm(f => ({ ...f, mobile: e.target.value }))} />
                                        </div>
                                        <div className="modal-field">
                                            <label>Project Type *</label>
                                            <select className="modal-url-input" value={createForm.project_type} onChange={e => setCreateForm(f => ({ ...f, project_type: e.target.value }))}>
                                                <option value="Website Development">Website Development</option>
                                                <option value="App Development">App Development</option>
                                                <option value="Both">Both</option>
                                            </select>
                                        </div>
                                        <div className="modal-field">
                                            <label>Project Name *</label>
                                            <input className="modal-url-input" value={createForm.project_name} onChange={e => setCreateForm(f => ({ ...f, project_name: e.target.value }))} />
                                        </div>
                                        <div className="modal-field" style={{ gridColumn: '1 / -1' }}>
                                            <label>Address *</label>
                                            <textarea className="modal-url-input" rows={2} value={createForm.address} onChange={e => setCreateForm(f => ({ ...f, address: e.target.value }))} />
                                        </div>
                                    </div>

                                    {/* Optional plan */}
                                    <div className="modal-plan-section">
                                        <label className="modal-plan-title"><FaIdCard /> Assign Plan (optional)</label>
                                        <div className="modal-plan-grid">
                                            <div className="modal-field">
                                                <label>Plan Name</label>
                                                <input className="modal-url-input" list="plan-presets-create" placeholder="e.g. Custom Plan" value={createForm.selected_plan} onChange={e => setCreateForm(f => ({ ...f, selected_plan: e.target.value }))} />
                                                <datalist id="plan-presets-create">
                                                    <option value=".in Plan" />
                                                    <option value=".com Plan" />
                                                    <option value="Custom Plan" />
                                                </datalist>
                                            </div>
                                            <div className="modal-field">
                                                <label>Custom Price (₹)</label>
                                                <input className="modal-url-input" type="number" min="0" step="any" placeholder="e.g. 1499" value={createForm.custom_plan_price} onChange={e => setCreateForm(f => ({ ...f, custom_plan_price: e.target.value }))} />
                                            </div>
                                            <div className="modal-field">
                                                <label>Duration (months)</label>
                                                <select className="modal-url-input" value={createForm.plan_duration} onChange={e => setCreateForm(f => ({ ...f, plan_duration: e.target.value }))}>
                                                    <option value="">Select…</option>
                                                    <option value="1">1 month</option>
                                                    <option value="3">3 months</option>
                                                    <option value="6">6 months</option>
                                                    <option value="12">12 months</option>
                                                </select>
                                            </div>
                                            <div className="modal-field">
                                                <label>Plan Start Date</label>
                                                <input className="modal-url-input" type="date" value={createForm.plan_start_date} onChange={e => setCreateForm(f => ({ ...f, plan_start_date: e.target.value }))} />
                                            </div>
                                        </div>
                                        <p className="modal-plan-hint">A random password is generated and emailed to the client. KYC can be uploaded later from their portal.</p>
                                    </div>

                                    <div className="modal-status-row">
                                        <button type="submit" className="modal-save-btn" disabled={creating} style={{ marginLeft: 'auto' }}>
                                            {creating ? 'Creating…' : 'Create Client'}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default AdminClients;
